import { FC } from 'react';
import {
    FormControl,
    TextField,
    FormLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FormQuestionDto, AnswerType } from '../onsite-visit.dto';
import dayjs, { Dayjs } from 'dayjs';
import AttachmentInput from './AttachmentInput';

interface QuestionRendererProps {
    question: FormQuestionDto;
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
    helperText?: string;
    t?: (key: string) => string;
}

export const QuestionRenderer: FC<QuestionRendererProps> = ({
    question,
    value,
    onChange,
    error,
    helperText,
    t = (key: string) => key,
}) => {
    const renderInputByType = () => {
        switch (question.answerType) {
            case AnswerType.TEXT:
                // Check if should be multiline based on validation max length
                const shouldBeMultiline = (question.config?.validation?.max ?? 0) > 100;
                return (
                    <TextField
                        fullWidth
                        label={question.question}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        error={error}
                        helperText={helperText}
                        required={question.config?.required}
                        placeholder={question.config?.placeholder || ''}
                        size="small"
                        multiline={shouldBeMultiline}
                        minRows={shouldBeMultiline ? 2 : undefined}
                        slotProps={{
                            htmlInput: {
                                maxLength: question.config?.validation?.max ?? undefined,
                            },
                        }}
                    />
                );

            case AnswerType.NUMBER:
                return (
                    <TextField
                        fullWidth
                        type="number"
                        label={question.question}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        error={error}
                        helperText={helperText}
                        required={question.config?.required}
                        placeholder={question.config?.placeholder || ''}
                        size="small"
                        slotProps={{
                            htmlInput: {
                                min: question.config?.validation?.min ?? undefined,
                                max: question.config?.validation?.max ?? undefined,
                            },
                        }}
                    />
                );

            case AnswerType.DROPDOWN:
                return (
                    <FormControl fullWidth error={error} size="small">
                        <FormLabel>{question.question}</FormLabel>
                        <Select
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            required={question.config?.required}
                        >
                            {question.config?.options?.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                        {helperText && (
                            <Typography variant="caption" color="error">
                                {helperText}
                            </Typography>
                        )}
                    </FormControl>
                );

            case AnswerType.CHECKBOX:
                return (
                    <FormControl error={error}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={value === 'true'}
                                    onChange={(e) => onChange(e.target.checked.toString())}
                                />
                            }
                            label={question.question}
                        />
                        {helperText && (
                            <Typography variant="caption" color="error">
                                {helperText}
                            </Typography>
                        )}
                    </FormControl>
                );

            case AnswerType.DATE_TIME:
                return (
                    <FormControl fullWidth error={error}>
                        <FormLabel>{question.question}</FormLabel>
                        <DateTimePicker
                            value={value ? dayjs(value) : null}
                            onChange={(newValue: Dayjs | null) => onChange(newValue ? newValue.toISOString() : '')}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    error: error,
                                    helperText: helperText,
                                    required: question.config?.required,
                                },
                            }}
                        />
                    </FormControl>
                );

            case AnswerType.MULTIPLE_CHOICE:
                return (
                    <FormControl fullWidth error={error} size="small">
                        <FormLabel>{question.question}</FormLabel>
                        <Select
                            multiple
                            value={value ? value.split(',') : []}
                            onChange={(e) => {
                                const selectedValues = e.target.value as string[];
                                onChange(selectedValues.join(','));
                            }}
                            required={question.config?.required}
                        >
                            {question.config?.options?.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                        {helperText && (
                            <Typography variant="caption" color="error">
                                {helperText}
                            </Typography>
                        )}
                    </FormControl>
                );

            case AnswerType.PHOTO:
                return (
                    <AttachmentInput
                        questionId={question.id}
                        question={question.question}
                        error={error || false}
                        onChangeResponse={onChange}
                        initialAttachments={question.attachments}
                        t={t}
                    />
                );

            case AnswerType.ANNOTATION:
                return (
                    <TextField
                        fullWidth
                        label={question.question}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        error={error}
                        helperText={helperText}
                        required={question.config?.required}
                        size="small"
                        multiline
                        minRows={3}
                        placeholder="Add annotations..."
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        label={question.question}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        error={error}
                        helperText={helperText}
                        required={question.config?.required}
                        size="small"
                    />
                );
        }
    };

    return renderInputByType();
};
